import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const MenuContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: var(--color-dark);
  background-image: radial-gradient(circle at 50% 50%, var(--color-dark-gray) 0%, var(--color-dark) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const GlassPanels = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const GlassPanel = styled(motion.div)`
  position: absolute;
  background: rgba(255, 144, 82, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 144, 82, 0.1);
  box-shadow: 0 0 20px rgba(255, 144, 82, 0.1);
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  padding: 0 2rem;
`;

const MenuTitle = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3.5rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  letter-spacing: 0.3rem;
  text-shadow: 0 0 15px rgba(255, 144, 82, 0.3);
  position: relative;
  
  span {
    color: var(--color-primary);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
    box-shadow: 0 0 10px rgba(255, 144, 82, 0.5);
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
    
    &::after {
      width: 80px;
      bottom: -10px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    
    &::after {
      width: 60px;
    }
  }
`;

const MenuItemsContainer = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  max-width: 1000px;
  perspective: 1000px;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const MenuItem = styled(motion.div)`
  position: relative;
  width: 280px;
  height: 180px;
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 144, 82, 0.1) 0%,
      rgba(255, 144, 82, 0) 100%
    );
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    width: 220px;
    height: 150px;
  }
  
  @media (max-width: 480px) {
    width: 160px;
    height: 120px;
  }
`;

const MenuItemIcon = styled(motion.div)`
  font-size: 3rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
  z-index: 1;
  filter: drop-shadow(0 0 10px rgba(255, 144, 82, 0.5));
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 0.7rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
`;

const MenuItemTitle = styled(motion.h3)`
  color: var(--color-white);
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    letter-spacing: 0.05rem;
  }
`;

const MenuItemDescription = styled(motion.p)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0.5rem 0 0;
  text-align: center;
  padding: 0 1rem;
  z-index: 1;
  max-width: 90%;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-top: 0.3rem;
  }
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const HeroOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--color-dark);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  will-change: transform;
`;

const HeroIcon = styled(motion.div)`
  font-size: 8rem;
  color: var(--color-primary);
  filter: drop-shadow(0 0 30px rgba(255, 144, 82, 0.7));
  z-index: 2;
  
  @media (max-width: 768px) {
    font-size: 6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 4rem;
  }
`;

const MainMenu: React.FC<{ onNavigate: (section: string) => void }> = ({ onNavigate }) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const menuItems = [
  { 
    id: 'journey', 
    label: 'Journey', 
    icon: '🗺️',
    description: 'Explore my professional trajectory and significant career milestones.'
  },
  { 
    id: 'skills', 
    label: 'Skills', 
    icon: '💪',
    description: 'Discover my technical skills and areas of expertise.'
  },
  { 
    id: 'projects', 
    label: 'Projects', 
    icon: '🏆',
    description: 'Check out the most relevant projects I have developed and their technologies.'
  },
  { 
    id: 'experiences', 
    label: 'Experiences', 
    icon: '📚',
    description: 'Learn about my professional experience and the challenges I have tackled.'
  },
  { 
    id: 'achievements', 
    label: 'Achievements', 
    icon: '🌟',
    description: 'Unlock achievements as you explore my interactive portfolio.'
  },
  { 
    id: 'contact', 
    label: 'Contact', 
    icon: '📧',
    description: 'Reach out to discuss collaboration opportunities.'
  }
];
  
  const handleNavigate = (section: string) => {
    setSelectedItem(section);
    
    // Aguardar a animação de transição antes de navegar
    setTimeout(() => {
      navigate(`/${section}`);
      setSelectedItem(null);
    }, 1000);
  };
  
  // Variantes para animações
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 24 
      }
    },
    hover: { 
      y: -10, 
      scale: 1.05,
      boxShadow: "0 0 30px rgba(255, 144, 82, 0.3)",
      borderColor: "rgba(255, 144, 82, 0.5)",
      transition: { 
        type: "spring", 
        stiffness: 400, 
        damping: 10 
      }
    },
    tap: { 
      scale: 0.95,
      boxShadow: "0 0 15px rgba(255, 144, 82, 0.2)",
    }
  };
  
  const iconVariants = {
    hidden: { scale: 0 },
    visible: { 
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 500, 
        damping: 15,
        delay: 0.2
      }
    },
    hover: { 
      scale: 1.2,
      transition: { 
        duration: 0.5
      }
    }
  };
  
  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: 0.3
      }
    },
    hover: { 
      color: "var(--color-primary)",
      textShadow: "0 0 8px rgba(255, 144, 82, 0.5)"
    }
  };
  
  const descriptionVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 0.7,
      transition: { 
        delay: 0.4
      }
    },
    hover: { 
      opacity: 1
    }
  };
  
  const heroOverlayVariants = {
    initial: { 
      clipPath: "circle(0% at center)",
      opacity: 0
    },
    animate: { 
      clipPath: "circle(100% at center)",
      opacity: 1,
      transition: { 
        duration: 0.8,
        ease: "easeInOut"
      }
    },
    exit: { 
      clipPath: "circle(100% at center)",
      opacity: 0,
      transition: { 
        duration: 0.5,
        ease: "easeInOut"
      }
    }
  };
  
  const heroIconVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { 
      scale: 1,
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 200, 
        damping: 20
      }
    },
    exit: { 
      scale: 1.5,
      opacity: 0,
      transition: { 
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  
  // Generate glass panels
  const glassPanelsData = [
    { width: '40%', height: '30%', top: '10%', left: '5%', rotate: -10 },
    { width: '30%', height: '40%', top: '60%', left: '10%', rotate: 15 },
    { width: '35%', height: '25%', top: '20%', right: '5%', rotate: 5 },
    { width: '25%', height: '35%', top: '55%', right: '15%', rotate: -8 },
    { width: '20%', height: '20%', top: '40%', left: '40%', rotate: 12 }
  ];

  return (
    <MenuContainer>
      {/* Glass panels in background */}
      <GlassPanels>
        {glassPanelsData.map((panel, index) => (
          <GlassPanel
            key={index}
            style={{
              width: panel.width,
              height: panel.height,
              top: panel.top,
              left: panel.left,
              right: panel.right,
              transform: `rotate(${panel.rotate}deg)`
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ 
              duration: 1,
              delay: index * 0.2
            }}
          />
        ))}
      </GlassPanels>
      
      <ContentWrapper
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <MenuTitle
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Caíque <span>Gonçalves</span>
        </MenuTitle>
        
        <MenuItemsContainer
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              variants={itemVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => handleNavigate(item.id)}
            >
              <MenuItemIcon
                variants={iconVariants}
              >
                {item.icon}
              </MenuItemIcon>
              <MenuItemTitle
                variants={titleVariants}
              >
                {item.label}
              </MenuItemTitle>
              <MenuItemDescription
                variants={descriptionVariants}
              >
                {item.description}
              </MenuItemDescription>
            </MenuItem>
          ))}
        </MenuItemsContainer>
      </ContentWrapper>
      
      {/* Animação de transição heroica */}
      <AnimatePresence mode="wait">
        {selectedItem && (
          <HeroOverlay
            variants={heroOverlayVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            key="hero-overlay"
          >
            <HeroIcon
              variants={heroIconVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              key="hero-icon"
            >
              {menuItems.find(item => item.id === selectedItem)?.icon}
            </HeroIcon>
          </HeroOverlay>
        )}
      </AnimatePresence>
    </MenuContainer>
  );
};

export default MainMenu;
